import type { TrapetsLookupInput } from "@repo/shared/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as trapetsLookupApi from "@/lib/api/flow-instances/trapets-lookup";
import { useInvalidateFlowCompletion } from "./use-flow-completion";
import { invalidateSuitabilityQueries } from "./use-goals";

const trapetsLookupKeys = {
  flowInstanceValue: (id: string | null) =>
    ["flowInstances", id, "trapets-lookup", "flow-instance-value"] as const,
};

export function useTrapetsLookupFlowInstanceValue(id: string | null) {
  const { data, error, isLoading } = useQuery({
    queryKey: trapetsLookupKeys.flowInstanceValue(id),
    queryFn: () => trapetsLookupApi.getFlowInstanceValue(id!),
    enabled: !!id,
  });
  return {
    inputData: data?.inputData ?? null,
    calculatedData: data?.calculatedData ?? null,
    error,
    isLoading,
  };
}

export function useSearchAndSaveTrapetsLookup(instanceId: string) {
  const queryClient = useQueryClient();
  const invalidateCompletion = useInvalidateFlowCompletion();
  return useMutation({
    mutationFn: (input: TrapetsLookupInput) => trapetsLookupApi.searchAndSave(instanceId, input),
    onSuccess: (result) => {
      if (result.success && result.inputData !== undefined) {
        queryClient.setQueryData(trapetsLookupKeys.flowInstanceValue(instanceId), {
          inputData: result.inputData,
          calculatedData: result.calculatedData,
        });
        // Slice 7 — completion predicate reads calculatedData.hasBeenChecked from this blob.
        invalidateCompletion(instanceId);
        // QL-654 — saving writes the typed identity back to the registered
        // representatives (and backfills the client), so everything showing
        // parties, the acting list or the Verified ID summary is stale.
        queryClient.invalidateQueries({ queryKey: ["clients"] });
        queryClient.invalidateQueries({ queryKey: ["client-identity"] });
        queryClient.invalidateQueries({ queryKey: ["flowInstances"] });
      }
    },
    onSettled: () => {
      invalidateSuitabilityQueries(queryClient);
    },
  });
}
